import { gql } from '@apollo/client'
import { BodyText, Heading } from '@farol-ds/react'
import { Price } from '@jusbrasil-web/shared-price'
import classNames from 'classnames'

import { LAWSUIT_NO_PROMO_EXPERIMENT_PRICE_REGEX } from '../../constants'
import { BenefitsList } from '../benefits-list'
import { CheckoutButton } from '../checkout-button'
import { OneClickButton } from '../one-click-button'
import {
  OneOfferLockContent_OfferLockDataFragment,
  OneOfferLockContent_ViewerFragment,
} from './__generated__/one-offer-lock-content.graphql'
import styles from './one-offer-lock-content.module.scss'

export interface OneOfferLockContentProps {
  offerLockData: OneOfferLockContent_OfferLockDataFragment
  viewer: OneOfferLockContent_ViewerFragment
}

export function OneOfferLockContent({ offerLockData, viewer }: OneOfferLockContentProps) {
  const { body, header } = offerLockData || {}
  const { title = '', subtitle = '' } = header || {}
  const {
    cards: [card],
  } = body || {}

  const isPromotional = !!card.offer.priceTitle
  // Mobile renders a single card (no siblings), so there's no lockHasPromoOffer
  // check like the desktop two-offer component — the price guard alone is enough.
  const isNoPromoLawsuitExperiment =
    !isPromotional &&
    !!card.offer.priceText &&
    LAWSUIT_NO_PROMO_EXPERIMENT_PRICE_REGEX.test(card.offer.priceText)
  const showPromotionalLayout = isPromotional || isNoPromoLawsuitExperiment
  const hasBenefits = !!card.benefitsList.length
  const cardClasses = classNames(styles['one-offer-lock-card'], {
    [styles['one-offer-lock-card--promotional']]: showPromotionalLayout && hasBenefits,
  })

  return (
    <>
      <div className={styles['one-offer-lock-header']}>
        <Heading size="lg" className={styles['one-offer-lock-header-title']}>
          {title}
        </Heading>
        <BodyText size="md" className={styles['one-offer-lock-header-subtitle']}>
          <span dangerouslySetInnerHTML={{ __html: subtitle }} />
        </BodyText>
      </div>

      <div className={cardClasses}>
        {hasBenefits && <BenefitsList offerLockCard={card} />}

        <div className={styles['one-offer-lock-card-wrapper-price']}>
          <Price align="center" size="lg">
            {card.offer.priceTitle && <Price.Title>{card.offer.priceTitle}</Price.Title>}
            <Price.Value>
              {card.offer.priceText}
              {card.offer.frequency && (
                <Price.ValueFrequency>{card.offer.frequency}</Price.ValueFrequency>
              )}
            </Price.Value>
          </Price>

          <CheckoutButton offerLockCard={card} kind="primary" viewer={viewer} />
          <OneClickButton offerLockCard={card} viewer={viewer} kind="primary" />
        </div>
      </div>
    </>
  )
}

OneOfferLockContent.fragments = {
  offerLockData: gql`
    fragment OneOfferLockContent_offerLockData on OfferLockData {
      header {
        title
        subtitle
      }
      body {
        cards {
          offer {
            priceTitle
            priceText
            frequency
          }
          ...OneClickButton_offerLockCard
          ...CheckoutButton_offerLockCard
          ...BenefitsList_offerLockCard
        }
      }
    }
    ${OneClickButton.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
    ${BenefitsList.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment OneOfferLockContent_viewer on Profile {
      ...OneClickButton_viewer
      ...CheckoutButton_viewer
    }
    ${OneClickButton.fragments.viewer}
    ${CheckoutButton.fragments.viewer}
  `,
}
